import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { saveGameScore, updateGameScore, generateUserId, fetchTotalScore, fetchUserScore } from '../../features/games/bicycleGameSlice';
import bicycleGameStyles from "../../styles/bicycleGame"
import { Box, Button, Grid, Typography, Link as MuiLink } from '@mui/material';
import GamePopup from './GamePopup';
import copite from '../../assets/images/bicycle_game/ecran2_copite.png'
import theme from '../../styles/themes/default';
import prize1 from '../../assets/images/bicycle_game/premiu1_hp.png'
import prize2 from '../../assets/images/bicycle_game/premiul2_hp.png'
import { keyframes } from '@mui/system';

const ALLOWED_ORIGIN = process.env.REACT_APP_GAME_ORIGIN || window.location.origin;

const flipAnimation = keyframes`
  0% {
    transform: rotateX(0);
  }
  50% {
    transform: rotateX(-90deg);
  }
  100% {
    transform: rotateX(0);
  }
`;

const BicycleGame: React.FC = () => {
  const dispatch = useAppDispatch();
  const { error, user, totalAllScore } = useAppSelector((state) => state.bicycleGame);
  const [showStartGame, setShowStartGame] = useState(!user);
  const formattedScore = totalAllScore.toString().padStart(3, '0');
  const [animatingDigits, setAnimatingDigits] = useState<number[]>([]);
  const [previousScore, setPreviousScore] = useState('000');
  const kmDigits = formattedScore.split('');
  const [score, setScore] = useState<number>(() => {
    const cooldownEnd = localStorage.getItem('bicycleCooldownEnd');
    const isCooldown = cooldownEnd && Date.now() < parseInt(cooldownEnd);
    
    if (isCooldown) {
        return 0;
    }
    
    const currentSessionScore = localStorage.getItem('currentSessionScore');
    if (currentSessionScore) {
        return Math.floor(parseFloat(currentSessionScore));
    }
    
    return 0;
});

const formattedTotalScore = totalAllScore.toString().padStart(3, '0');

// Adăugăm stări pentru detecția orientării dispozitivului
const [isMobile, setIsMobile] = useState(false);
const [isPortrait, setIsPortrait] = useState(true);
const iframeRef = useRef<HTMLIFrameElement>(null);

const [showPopup, setShowPopup] = useState(false);

  const [gameStartScore, setGameStartScore] = useState<number>(() => {
    return parseFloat(localStorage.getItem('bicycleTotalScore') || '0');
  });

  const [processedMilestones, setProcessedMilestones] = useState<Set<number>>(() => {
    const saved = localStorage.getItem('processedMilestones');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  const [lastProcessedScore, setLastProcessedScore] = useState<number>(0);
  const [lastSavedMilestone, setLastSavedMilestone] = useState<number>(() => {
    const savedMilestone = localStorage.getItem('lastSavedMilestone');
    return savedMilestone ? parseInt(savedMilestone) : 0;
  });
  const [totalScore, setTotalScore] = useState<number>(() => {
    const savedTotal = localStorage.getItem('bicycleTotalScore');
    return savedTotal ? parseFloat(savedTotal) : 0;
  });
  const [isInCooldown, setIsInCooldown] = useState<boolean>(() => {
    const cooldownEnd = localStorage.getItem('bicycleCooldownEnd');
    return cooldownEnd ? Date.now() < parseInt(cooldownEnd) : false;
  });
  const [cooldownEndTime, setCooldownEndTime] = useState<number>(() => {
    return parseInt(localStorage.getItem('bicycleCooldownEnd') || '0');
  });

  const classes = bicycleGameStyles();

  // Adăugăm useEffect pentru a detecta orientarea dispozitivului
  useEffect(() => {
    const checkIfMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };
    
    setIsMobile(checkIfMobile());
    
    // Reajustăm la redimensionare
    const handleResize = () => {
      const mobile = checkIfMobile();
      setIsMobile(mobile);
      
      // Verificăm orientarea doar dacă este dispozitiv mobil
      if (mobile) {
        const portrait = window.matchMedia("(orientation: portrait)").matches;
        setIsPortrait(portrait);
      } else {
        // Pe desktop, considerăm întotdeauna că este "portrait" pentru a afișa jocul
        setIsPortrait(true);
      }
    };
    
    // Folosim matchMedia pentru o detecție mai precisă
    const mediaQuery = window.matchMedia("(orientation: portrait)");
    const handleOrientationChange = (e: MediaQueryListEvent) => {
      if (isMobile) {
        setIsPortrait(e.matches);
      }
    };
    
    // Adăugăm listener pentru schimbarea orientării
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleOrientationChange);
    } else {
      // Pentru browsere mai vechi
      mediaQuery.addListener(handleOrientationChange);
    }
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Verificare inițială
    
    // Ascultăm mesaje de la iframe
    const handleMessage = (event: MessageEvent) => {
      // Verificăm doar mesajele de la joc
      if (event.data && event.data.type === 'orientation') {
        // Orientarea este gestionată nativ prin detectarea matchMedia
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('message', handleMessage);
      
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleOrientationChange);
      } else {
        mediaQuery.removeListener(handleOrientationChange);
      }
    };
  }, [isMobile]);

  const handleStartGame = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await dispatch(generateUserId()).unwrap();
      
      if (result) {
        localStorage.setItem('bicycleGameUserId', result);
        setShowStartGame(false);
      }
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };
  
  const verifyTotalScoreIncrease = async (newTotal: number) => {
    if (!user) return true;
    
    const increase = newTotal - totalScore;
    if (increase > 15 || (newTotal - totalScore) > 1) {
      
      try {
        const backendScore = await dispatch(fetchUserScore(user)).unwrap();
        
        if (typeof backendScore === 'number' && backendScore >= 0) {
          setTotalScore(backendScore);
          // Actualizăm și gameStartScore pentru a menține sincronizarea
          setGameStartScore(backendScore);
          localStorage.setItem('bicycleTotalScore', String(backendScore));
          
          setScore(0);
          setLastSavedMilestone(0);
          setProcessedMilestones(new Set());
          localStorage.setItem('currentSessionScore', '0');
          localStorage.setItem('lastSavedMilestone', '0');
          localStorage.setItem('processedMilestones', JSON.stringify([]));
          
          const cooldownEnd = Date.now() + 60000;
          setIsInCooldown(true);
          setCooldownEndTime(cooldownEnd);
          localStorage.setItem('bicycleCooldownEnd', cooldownEnd.toString());
          
          return false;
        }
      } catch (error) {
        return false;
      }
    }
    return true;
};

const saveScore = (newTotal: number, isFirstSave: boolean) => {
  if (!user) return;

  if (isFirstSave) {
    dispatch(saveGameScore({ score: newTotal, unique_id: user }));
    localStorage.setItem(`bicycleGameFirstSave_${user}`, 'true');
  } else {
    dispatch(updateGameScore({ score: newTotal, unique_id: user }));
  }
};

  useEffect(() => {
    const fetchTeamScore = () => {
      dispatch(fetchTotalScore());
    };

    fetchTeamScore();
    const teamScoreInterval = setInterval(fetchTeamScore, 30000);

    return () => {
      clearInterval(teamScoreInterval);
    };
  }, [dispatch]);

 useEffect(() => {
     if (previousScore !== formattedScore) {
       // Find which digits have changed
       const changedDigits: number[] = [];
       for (let i = 0; i < formattedScore.length; i++) {
         if (previousScore[i] !== formattedScore[i]) {
           changedDigits.push(i);
         }
       }
       
       // Set which digits should animate
       setAnimatingDigits(changedDigits);
       
       // Clear animation state after animation completes
       const timer = setTimeout(() => {
         setAnimatingDigits([]);
         // Update previous score after animation completes
         setPreviousScore(formattedScore);
       }, 1000);
       
       return () => clearTimeout(timer);
     }
   }, [formattedScore, previousScore]);

  useEffect(() => {
    if (isInCooldown) {
        const interval = setInterval(() => {
            if (Date.now() >= cooldownEndTime) {
                setIsInCooldown(false);
                setScore(0);
                setLastSavedMilestone(0);
                setProcessedMilestones(new Set());
                
                localStorage.removeItem('bicycleCooldownEnd');
                localStorage.removeItem('currentSessionScore');
                localStorage.removeItem('processedMilestones');
                localStorage.removeItem('lastSavedMilestone');
                localStorage.setItem('justFinishedCooldown', 'true');
                
                // Forțăm un reload al iframe-ului
                const iframe = document.querySelector('iframe');
                if (iframe) {
                    iframe.src = iframe.src;
                }
            }
        }, 1000);
    
        return () => clearInterval(interval);
    }
}, [isInCooldown, cooldownEndTime]);

  useEffect(() => {
    const handleScoreUpdate = async (event: MessageEvent) => {
      if (event.origin !== ALLOWED_ORIGIN) {
        return;
      }
      
      if (!user) return;

      if (event.data?.type === 'showPopup') {
        setShowPopup(true);
      }
    
      if (event.data?.type === 'currentScore' || event.data?.type === 'gameEnd') {
        const wasInCooldown = localStorage.getItem('justFinishedCooldown');
        if (wasInCooldown) {
            localStorage.removeItem('justFinishedCooldown');
            localStorage.removeItem('currentSessionScore');
            return;
        }
    
        const currentScore = Math.floor(Number(event.data.score) / 10);
        const previousScore = Math.floor(parseFloat(localStorage.getItem('currentSessionScore') || '0'));
        
        if (currentScore - previousScore > 15) {
          const cooldownEnd = Date.now() + 60000;
          setIsInCooldown(true);
          setCooldownEndTime(cooldownEnd);
          localStorage.setItem('bicycleCooldownEnd', cooldownEnd.toString());
          localStorage.removeItem('currentSessionScore');
          return;
      }
        
        if (currentScore >= 15 && !isInCooldown) {
          const cooldownEnd = Date.now() + 60000;
          setIsInCooldown(true);
          setCooldownEndTime(cooldownEnd);
          localStorage.setItem('bicycleCooldownEnd', cooldownEnd.toString());
        }
    
        if (currentScore > lastSavedMilestone && !isInCooldown) {
          const existingTotal = Math.floor(parseFloat(localStorage.getItem('bicycleTotalScore') || '0'));
          const newMilestonesArray = [];
          
          for (let i = lastSavedMilestone + 1; i <= currentScore; i++) {
            if (!processedMilestones.has(i)) {
              newMilestonesArray.push(i);
              processedMilestones.add(i);
            }
          }
          
          if (newMilestonesArray.length > 0) {
            const newTotal = existingTotal + newMilestonesArray.length;
            localStorage.setItem('bicycleTotalScore', String(newTotal));
            localStorage.setItem('processedMilestones', JSON.stringify(Array.from(processedMilestones)));
            setTotalScore(newTotal);
            
            // Salvează în backend
            const savedScore = localStorage.getItem(`bicycleGameFirstSave_${user}`);
            saveScore(newTotal, !savedScore);
          }

          if (newMilestonesArray.length > 0) {
              const newTotal = Number((existingTotal + newMilestonesArray.length).toFixed(1));
              
              // Verifică dacă creșterea scorului este validă
              const isValidIncrease = await verifyTotalScoreIncrease(newTotal);
              
              if (isValidIncrease) {
                localStorage.setItem('bicycleTotalScore', String(newTotal));
                localStorage.setItem('processedMilestones', JSON.stringify(Array.from(processedMilestones)));
                setTotalScore(newTotal);
                
                // Save to backend
                const savedScore = localStorage.getItem(`bicycleGameFirstSave_${user}`);
                saveScore(newTotal, !savedScore);
              }
            }
          
          setLastSavedMilestone(currentScore);
          localStorage.setItem('lastSavedMilestone', String(currentScore));
        }
      }
    };

    const scoreInterval = setInterval(() => {
      const iframe = document.querySelector('iframe');
      if (iframe?.contentWindow && !isInCooldown) {
        iframe.contentWindow.postMessage({ type: 'getCurrentScore' }, ALLOWED_ORIGIN);
      }
    }, 2000);

    window.addEventListener('message', handleScoreUpdate);

    return () => {
      clearInterval(scoreInterval);
      window.removeEventListener('message', handleScoreUpdate);
    };
  }, [score, lastProcessedScore, totalScore, dispatch, user, lastSavedMilestone, isInCooldown]);

  useEffect(() => {
    if (score === 0) {
      setLastProcessedScore(0);
      setLastSavedMilestone(0);
    }
  }, [score]);

  useEffect(() => {
    const verifyStoredScore = async () => {
      if (!user) return;
  
      const storedTotal = parseFloat(localStorage.getItem('bicycleTotalScore') || '0');
      
      try {
        const backendScore = await dispatch(fetchUserScore(user)).unwrap();
        
        if (storedTotal > backendScore && typeof backendScore === 'number' && backendScore > 0) {
          setTotalScore(backendScore);
          setScore(0);
          localStorage.setItem('bicycleTotalScore', String(backendScore));
          localStorage.setItem('currentSessionScore', '0');
        }
      } catch (error) {
        // Nu mai resetăm scorul în caz de eroare
      }
    };
  
    verifyStoredScore();
}, [user]);

if (showStartGame) {
  return (
    <div className={classes.gameContainer}>
      <div className={classes.nicknameForm}>
        <Button 
          onClick={handleStartGame}
          className={classes.button}
        >
          Start Game
        </Button>
      </div>
    </div>
  );
}

  return (
    <div className={classes.gameContainer}>
     
      <div className={classes.totalScoreText}>
        Kilometri pedalați: {totalScore.toFixed(1)} km
      </div>
      
      <GamePopup 
        isOpen={showPopup}
        onClose={() => {
          setShowPopup(false);
          const iframe = document.querySelector('iframe');
          if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage({ 
              type: 'resumeGame',
              action: 'unpause'
            }, ALLOWED_ORIGIN);
          }
        }}
        score={score}
      />
      {isInCooldown && (
  <div className={classes.cooldownMessage}>
    <div>
      TAPĂN! Ai atins limita de 15 KM. Ia o pauză de  {Math.ceil((cooldownEndTime - Date.now()) / 1000)} secunde și poti pedala din nou. Have a nice ride!
    </div>
  </div>
)}
      {isMobile && !isPortrait ? (
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: 3
          }}
        >
          <Typography variant="h5" sx={{ color: '#A32016', textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
            Te rugăm să rotești dispozitivul în pozitie verticală pentru a juca.
          </Typography>
        </Box>
      ) : (
        <>
          <iframe
            ref={iframeRef}
            src="/game/game/index.html"
            title="My Game"
            width="100%"
            height={isMobile ? "400x" : "600px"}
            style={{ 
              border: 'none',
              outline: 'none',
              background: 'transparent',
              opacity: isInCooldown ? 0.5 : 1,
              pointerEvents: isInCooldown ? 'none' : 'auto',
              maxHeight: '100vh'
            }}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          />

          <Typography variant="h3" sx={{ color: '#3e2e1a', textAlign: 'center', mt: 5, fontFamily: theme.fonts.poplar }}>
            Mulțumim că ne ești alături
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 3 
          }}>
            <Typography variant="h4" sx={{color: '#3e2e1a', textAlign: 'center' }}>
              Până acum, comunitatea a strâns 
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                marginY: 2
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', marginRight: 1 }}>
                <Box
                  component="img"
                  src={copite}
                  alt="Kozel copite"
                  sx={{ 
                    height: '20px',
                    width: 'auto',
                    marginRight: '5px'
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex' }}>
            {kmDigits.map((digit, index) => (
              <Box 
                key={index}
                sx={{
                  position: 'relative',
                  width: { xs: 45, sm: 60 },
                  height: { xs: 60, sm: 80 },
                  margin: { xs: '0 2px', sm: '0 5px' },
                  borderRadius: '8px',
                  overflow: 'visible',
                }}
              >
                <Box 
                   sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#3e2e1a',
                    color: 'white',
                    fontSize: { xs: '3.5rem', sm: '5rem' },
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
                    fontFamily: theme.fonts.apex,
                    paddingTop: { xs: '5px', sm: '10px' },
                    animation: animatingDigits.includes(index) 
                      ? `${flipAnimation} 0.7s ease-in-out` 
                      : 'none',
                    transformStyle: 'preserve-3d',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      zIndex: 3,
                    }
                  }}
                >
                  {digit}
                  
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      width: '100%',
                      height: '2px',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      zIndex: 10,
                    }}
                  />
                </Box>
                
                <Box 
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '85%',
                    transform: 'translateY(-50%)',
                    width: { xs: 4, sm: 6 },
                    height: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    zIndex: 5,
                  }}
                >
                  <Box sx={{ width: '100%',
                              height: 12,
                              backgroundColor: '#3e2e1a',
                              borderRadius: '2px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                              border: '1px solid #2a1f12'
                              }} />  
                </Box>
                
                <Box 
                  sx={{
                    position: 'absolute',
                    right: 1,
                    top: '85%',
                    transform: 'translateY(-50%)',
                    width: { xs: 4, sm: 6 },
                    height: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    zIndex: 5,
                  }}
                >
                  <Box sx={{ 
                     width: '100%',
                     height: 12,
                      backgroundColor: '#3e2e1a',
                      borderRadius: '2px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                      border: '1px solid #2a1f12'
                      }} />
                </Box>
              </Box>
            ))}
          </Box>
              
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', marginLeft: 1 }}>
                <Box
                  component="img"
                  src={copite}
                  alt="Kozel copite"
                  sx={{ 
                    height: '20px',
                    width: 'auto',
                    marginRight: '5px'
                  }}
                />
              </Box>
          </Box>
            <Typography 
                variant="h4" 
                sx={{ 
                  color: '#3e2e1a', 
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginTop: 1,
                  mb: 3
                }}
              >
                Kilometri
              </Typography>

              <Typography 
                  variant="h3" 
                  component="h3" 
                  sx={{ 
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    //fontWeight: 700,
                    color: '#3e2e1a',
                    textTransform: 'uppercase',
                    fontFamily: theme.fonts.apex
                  }}
                >
                  POȚI CÂȘTIGA:
                </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {/* Premiul 1 - coloana stângă pe desktop, centrat pe mobil */}
              <Grid item xs={12} sm={12} sx={{ 
                display: 'flex', 
                justifyContent: { 
                  xs: 'center',
                  sm: 'center'
                }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 4, 
                  maxWidth: '350px',
                  width: { xs: '300px', sm: 'auto' }
                }}>
                  <Box
                    sx={{
                      width: { xs: 100, sm: 100 },
                      height: { xs: 100, sm: 100 },
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: { xs: 2, sm: 3 },
                      overflow: 'hidden',
                      boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
                      flexShrink: 0,
                      aspectRatio: '1/1',
                    }}
                  >
                    <Box
                      component="img"
                      src={prize1}
                      alt="Bicicletă electrică"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#3e2e1a',
                        mb: 1
                      }}
                    >
                      Prin tragere la sorți:
                    </Typography>
                    <Typography 
                      variant="h6" 
                      component="p" 
                      sx={{ 
                        color: '#3e2e1a',
                        fontWeight: 'bold',
                        fontFamily: theme.fonts.apex
                      }}
                    >
                      3 BICICLETE<br />ELECTRICE
                    </Typography>
                       <span style={{
                          fontStyle: 'italic',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                        }}>
                          <MuiLink 
                          href="/pdf/2025/Regulament autentificat_Kozel @ online.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#3e2e1a',
                            fontStyle: 'italic',
                            textDecoration: 'underline',
                            fontWeight: 'bold',
                            '&:hover': {
                              color: '#ffffff'
                            }
                          }}
                        >
                         Vezi regulament
                        </MuiLink>
                    </span>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ 
                display: 'flex', 
                justifyContent: { 
                  xs: 'center',
                  sm: 'flex-start'
                }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 4, 
                  maxWidth: '350px',
                  width: { xs: '300px', sm: 'auto' }
                }}>
                  {/* <Box
                    sx={{
                      width: { xs: 100, sm: 100 },
                      height: { xs: 100, sm: 100 },
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: { xs: 2, sm: 3 },
                      overflow: 'hidden',
                      boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
                      flexShrink: 0,
                      aspectRatio: '1/1',
                    }}
                  >
                    <Box
                      component="img"
                      src={prize2}
                      alt="Cupon 20% discount"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </Box> */}
                  {/* <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#3e2e1a',
                        mb: 1
                      }}
                    >
                      Pe loc:
                    </Typography>
                    <Typography 
                      variant="h6" 
                      component="p" 
                      sx={{ 
                        color: '#3e2e1a',
                        fontWeight: 'bold',
                        fontFamily: theme.fonts.apex
                      }}
                    >
                      VOUCHERE 20% DISCOUNT<br />KOZEL IN KAUFLAND
                    </Typography>
                       <span style={{
                          fontStyle: 'italic',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                        }}>
                          <MuiLink 
                          href="/pdf/2025/Regulament_Kozel_Kaufland_final.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#3e2e1a',
                            fontStyle: 'italic',
                            textDecoration: 'underline',
                            fontWeight: 'bold',
                            '&:hover': {
                              color: '#ffffff'
                            }
                          }}
                        >
                          Vezi regulament
                        </MuiLink>
                    </span>
                  </Box> */}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </div>
  );
};

export default BicycleGame;